import { FileButton, ModuleLayout, Space, Text, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { DocumentStaticActionType } from '@views/documents/elements/DocumentStaticActions'
import { FileList, FileListSkeleton } from '@views/documents/elements/FileList'

import { ModalId } from '../../../../enums/modalId'
import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { UNSORTED_FOLDER_ID } from '../../constants'
import { MAX_FILE_SIZE_IN_BYTES } from '../../constants/uploadConstants'
import { DocumentPreviewModal } from '../../elements/DocumentPreviewModal'
import { DocumentsActionsBar } from '../../elements/DocumentsActionsBar'
import { DocumentsDropzone } from '../../elements/DocumentsDropzone'
import { SharedDocumentsEmptyState } from '../../elements/SharedDocumentsEmptyState'
import { useCopyDocumentOrFolderLink } from '../../hooks/useCopyDocumentOrFolderLink'
import { useDmsUserRole } from '../../hooks/useDmsUserRole'
import { useDocumentListState } from '../../hooks/useDocumentListState'
import { useDocumentPreviewActions } from '../../hooks/useDocumentPreviewActions'
import { useOpenDocumentIdFromStorage } from '../../hooks/useOpenDocumentIdFromStorage'
import { useUploadDocuments } from '../FolderContent/hooks/useUploadDocuments'

export const SharedByMe = (): ReactElement => {
  const [fileListKey, setFileListKey] = useState(0)
  const { t } = useTranslation()
  const { clearOpenDocumentId, openDocumentId } = useOpenDocumentIdFromStorage()
  const { close: closePreviewModal, open: openPreviewModal } = useModal(ModalId.DocumentsPreviewModal)
  const { organization } = useUserOrganization()
  const { isLoading, isBoWithoutCpa, isCpaExpertMode } = useDmsUserRole()
  const { copyLink } = useCopyDocumentOrFolderLink()
  const { isUploading, uploadDocuments } = useUploadDocuments({ cacheKey: UNSORTED_FOLDER_ID, folderId: undefined })

  const handleDeleteSuccess = useCallback(() => {
    setFileListKey((prev) => prev + 1)
  }, [])

  const {
    deleteItem,
    documents,
    filteredItems,
    handleCheckedChange,
    handleSearchChange,
    isEmpty,
    isFolderContentsLoading,
    searchQuery,
    selectedIds,
  } = useDocumentListState({ onDeleteSuccess: handleDeleteSuccess })

  const { selectedDocument, handleRowClick, handlePreviewModalAction, selectAndOpenDocument } =
    useDocumentPreviewActions({
      documents,
      deleteItem,
      closePreviewModal,
      openPreviewModal,
      onCopyLink: (document) => copyLink(document.id, 'document'),
    })

  const handleCopyLink = useCallback((id: string) => copyLink(id, 'document'), [copyLink])

  const handleDocumentsActionsBarDelete = useCallback(() => undefined, [])

  const handleFilesSelect = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length > 0 || rejectedFiles.length > 0) {
        uploadDocuments(acceptedFiles, rejectedFiles)
      }
    },
    [uploadDocuments],
  )

  useEffect(() => {
    if (!openDocumentId || isFolderContentsLoading || !documents.some((document) => document.id === openDocumentId)) {
      return
    }

    selectAndOpenDocument(openDocumentId)
    clearOpenDocumentId()
  }, [clearOpenDocumentId, documents, isFolderContentsLoading, openDocumentId, selectAndOpenDocument])

  if (isLoading) {
    return (
      <ModuleLayout fullHeight title={t('documents.shared_by_me.title')}>
        <FileListSkeleton />
      </ModuleLayout>
    )
  }

  if (isBoWithoutCpa || isCpaExpertMode) {
    const redirectRoute = isCpaExpertMode
      ? reactRoute.organization.documents.getMainRoute()
      : reactRoute.organization.clientDocuments.getMainRoute()

    return <Navigate to={getOrganizationViewUrl(organization?.url, redirectRoute)} replace />
  }

  return (
    <>
      <ModuleLayout
        fullHeight
        sideActions={
          <FileButton
            accept="all"
            icon="upload"
            loading={isUploading}
            maxSize={MAX_FILE_SIZE_IN_BYTES}
            multiple
            onFilesSelect={handleFilesSelect}
          >
            {t('documents.folder_content.top_bar.upload')}
          </FileButton>
        }
        title={t('documents.shared_by_me.title')}
        withMenuOffset={false}
      >
        <Text colorVariant="secondary" variant="bodyMD">
          {t('documents.shared_by_me.subtitle')}
        </Text>
        <Space size="s24" />

        <DocumentsActionsBar
          isEmpty={isEmpty}
          isLoading={isFolderContentsLoading}
          onDelete={handleDocumentsActionsBarDelete}
          onSearchChange={handleSearchChange}
          placeholder={t('documents.shared_by_me.search_placeholder')}
          searchQuery={searchQuery}
          selectedIds={selectedIds}
        />

        <DocumentsDropzone heightOffset={210} isDisabled={false} onFilesSelect={handleFilesSelect}>
          {filteredItems.length === 0 && !isFolderContentsLoading ? (
            <SharedDocumentsEmptyState isBoWithCpa />
          ) : (
            <FileList
              key={fileListKey}
              isLoading={isFolderContentsLoading}
              items={filteredItems}
              onCopyLink={handleCopyLink}
              onCheckedChange={handleCheckedChange}
              onDelete={deleteItem}
              onRowClick={handleRowClick}
              restrictedActions={[DocumentStaticActionType.CopyLink, DocumentStaticActionType.Delete]}
              selectedCount={selectedIds.length}
              showCheckboxes={false}
              showContextMenu
            />
          )}
        </DocumentsDropzone>
      </ModuleLayout>
      {selectedDocument && (
        <DocumentPreviewModal
          document={selectedDocument}
          id={ModalId.DocumentsPreviewModal}
          onAction={handlePreviewModalAction}
          showDownloadButton={false}
          visibleActions={[DocumentStaticActionType.CopyLink, DocumentStaticActionType.Delete]}
        />
      )}
    </>
  )
}
